import React from "react";

export const SA = {
  ink: "#344767",
  inkDeep: "#21243d",
  muted: "#8392ab",
  border: "#e9ecef",
  bgSoft: "#f8f9fe",
  primary: "#5e72e4",
  primary2: "#825ee4",
  danger: "#f5365c",
  success: "#2dce89",
  radiusLg: "18px",
  radiusMd: "10px",
  shadow: "0 2px 24px rgba(52,71,103,0.09)",
  gradientHdr: "linear-gradient(135deg,#344767 0%,#21243d 100%)",
  gradientBtn: "linear-gradient(135deg,#5e72e4,#825ee4)",
};

export function SaPageShell({ children, style, constrained }) {
  return (
    <div
      style={{
        padding: "24px 20px 40px",
        width: "100%",
        maxWidth: constrained ? "1320px" : "100%",
        marginLeft: constrained ? "auto" : undefined,
        marginRight: constrained ? "auto" : undefined,
        boxSizing: "border-box",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function SaPageHeader({ title, subtitle, actions }) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "16px",
        marginBottom: "24px",
      }}
    >
      <div>
        <h1
          style={{
            margin: "0 0 6px",
            fontSize: "22px",
            fontWeight: "800",
            color: SA.ink,
            letterSpacing: "-0.02em",
          }}
        >
          {title}
        </h1>
        {subtitle ? (
          <p style={{ margin: 0, fontSize: "14px", color: SA.muted, maxWidth: "560px" }}>{subtitle}</p>
        ) : null}
      </div>
      {actions ? <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>{actions}</div> : null}
    </div>
  );
}

export function SaPrimaryButton({ children, onClick, type = "button", disabled, iconClass }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        border: "none",
        borderRadius: SA.radiusMd,
        padding: "10px 20px",
        fontSize: "13px",
        fontWeight: "700",
        color: "#fff",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.65 : 1,
        background: SA.gradientBtn,
        boxShadow: disabled ? "none" : "0 4px 12px rgba(94,114,228,0.35)",
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
      }}
    >
      {iconClass ? <i className={iconClass} /> : null}
      {children}
    </button>
  );
}

export function SaGhostButton({ children, onClick, type = "button" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      style={{
        background: "transparent",
        border: `1.5px solid ${SA.border}`,
        borderRadius: SA.radiusMd,
        padding: "10px 18px",
        fontSize: "13px",
        fontWeight: "600",
        color: SA.muted,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

/** Right-aligned row for form Save / Cancel / secondary actions. */
export function SaFormActions({ children, bordered = false, style }) {
  return (
    <div
      style={{
        marginTop: bordered ? "20px" : "12px",
        marginBottom: "-4px",
        paddingTop: bordered ? "18px" : 0,
        borderTop: bordered ? `1px solid ${SA.border}` : "none",
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: "12px",
        flexWrap: "wrap",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function SaCard({ children, noPadding }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: SA.radiusLg,
        boxShadow: SA.shadow,
        overflow: "hidden",
      }}
    >
      <div style={noPadding ? undefined : { padding: "24px 28px" }}>{children}</div>
    </div>
  );
}

export function SaTabs({ tabs, activeKey, onChange }) {
  return (
    <div
      role="tablist"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        padding: "8px",
        background: SA.bgSoft,
        borderRadius: SA.radiusMd,
        marginBottom: "20px",
      }}
    >
      {tabs.map((t) => {
        const active = activeKey === t.key;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.key)}
            style={{
              border: "none",
              borderRadius: "8px",
              padding: "10px 16px",
              fontSize: "13px",
              fontWeight: "700",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: active ? "#fff" : "transparent",
              color: active ? SA.primary : SA.muted,
              boxShadow: active ? "0 2px 8px rgba(52,71,103,0.08)" : "none",
              transition: "background .15s,color .15s",
            }}
          >
            {t.icon ? <i className={t.icon} style={{ opacity: 0.9 }} /> : null}
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/** Horizontal child tabs for Integration sources (Facebook, Google Ads, etc.). */
export function SaIntegrationSubTabs({ tabs, activeKey, onChange }) {
  return (
    <div
      role="tablist"
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "8px",
        padding: "10px 12px",
        background: SA.bgSoft,
        borderRadius: SA.radiusMd,
        marginBottom: "20px",
        border: `1px solid ${SA.border}`,
      }}
    >
      {tabs.map((t) => {
        const active = activeKey === t.key;
        const iconColor = active ? t.iconColor || SA.primary : SA.muted;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.key)}
            style={{
              border: "none",
              borderRadius: "10px",
              padding: "11px 18px",
              fontSize: "13px",
              fontWeight: "700",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              background: active ? "#fff" : "transparent",
              color: active ? SA.primary : SA.muted,
              boxShadow: active ? "0 2px 10px rgba(52,71,103,0.1)" : "none",
              transition: "background .15s,color .15s,box-shadow .15s",
              whiteSpace: "nowrap",
            }}
          >
            {t.icon ? (
              <i
                className={t.icon}
                style={{
                  fontSize: "15px",
                  color: iconColor,
                  transition: "color .15s",
                }}
              />
            ) : null}
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

export function SaField({ label, children, hint }) {
  return (
    <div style={{ marginBottom: "18px" }}>
      <label
        style={{
          display: "block",
          fontSize: "11px",
          fontWeight: "700",
          color: SA.muted,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          marginBottom: "6px",
        }}
      >
        {label}
      </label>
      {children}
      {hint ? (
        <p style={{ margin: "6px 0 0", fontSize: "12px", color: SA.muted }}>{hint}</p>
      ) : null}
    </div>
  );
}

export const saInputSx = {
  width: "100%",
  padding: "11px 14px",
  border: `1.5px solid ${SA.border}`,
  borderRadius: SA.radiusMd,
  fontSize: "14px",
  color: SA.ink,
  boxSizing: "border-box",
  outline: "none",
};

export function SaSectionTitle({ iconClass, children }) {
  return (
    <h2
      style={{
        margin: "0 0 18px",
        fontSize: "15px",
        fontWeight: "800",
        color: SA.ink,
        display: "flex",
        alignItems: "center",
        gap: "10px",
      }}
    >
      {iconClass ? <i className={iconClass} style={{ color: SA.primary }} /> : null}
      {children}
    </h2>
  );
}
